import { type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { GridColDef, GridRowId, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';

import { DataGridMasterDetailColumnsContext } from './DataGridMasterDetail.context';
import { MasterDetailDataGrid, MasterDetailRoot } from './DataGridMasterDetail.styled';

export type DataGridMasterDetailColumn<R extends GridValidRowModel> = GridColDef<R> & {
  /** When false, column is hidden on master and matching child fields. */
  visible?: boolean;
};

export type DataGridMasterDetailRow = GridValidRowModel & {
  id: string | number;
  /** When false, row is omitted. Defaults to true. */
  visible?: boolean;
};

export type DataGridMasterDetailProps<R extends DataGridMasterDetailRow> = {
  columns: DataGridMasterDetailColumn<R>[];
  rows: R[];
  /**
   * Default collapsed state for uncontrolled expansion.
   * `false` = all visible rows expanded (default).
   */
  collapsed?: boolean;
  /**
   * Controlled expanded row ids.
   * @see https://mui.com/x/react-data-grid/master-detail/#controlling-expanded-detail-panels
   */
  detailPanelExpandedRowIds?: Set<GridRowId>;
  onDetailPanelExpandedRowIdsChange?: (ids: Set<GridRowId>) => void;
  getDetailPanelContent?: (params: GridRowParams<R>) => ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<R>) => number | 'auto';
  getRowId?: (row: R) => GridRowId;
  getRowClassName?: (params: GridRowParams<R>) => string;
  columnHeaderHeight?: number;
  rowHeight?: number;
  autoHeight?: boolean;
  dataTestID?: string;
};

/**
 * Master-detail Data Grid with dynamic column/row visibility.
 * @see https://mui.com/x/react-data-grid/master-detail/
 */
export function DataGridMasterDetail<R extends DataGridMasterDetailRow>({
  columns,
  rows,
  collapsed = false,
  detailPanelExpandedRowIds: controlledExpandedIds,
  onDetailPanelExpandedRowIdsChange,
  getDetailPanelContent,
  getDetailPanelHeight = () => 'auto',
  getRowId = (row) => row.id,
  getRowClassName,
  columnHeaderHeight = 48,
  rowHeight = 48,
  autoHeight = true,
  dataTestID = 'data-grid-master-detail',
}: Readonly<DataGridMasterDetailProps<R>>): ReactElement {
  const visibleRows = useMemo(() => rows.filter((row) => row.visible !== false), [rows]);

  const { gridColumns, hiddenFields, visibleFields } = useMemo(() => {
    const hidden = new Set<string>();
    const visible: string[] = [];
    const nextColumns: GridColDef<R>[] = [];

    for (const column of columns) {
      const { visible: columnVisible = true, ...rest } = column;
      if (!columnVisible) {
        hidden.add(column.field);
        continue;
      }
      visible.push(column.field);
      nextColumns.push(rest);
    }

    return {
      gridColumns: nextColumns,
      hiddenFields: hidden,
      visibleFields: visible,
    };
  }, [columns]);

  const columnsContextValue = useMemo(() => ({ hiddenFields, visibleFields }), [hiddenFields, visibleFields]);

  const defaultExpandedIds = useMemo(
    () => new Set<GridRowId>(collapsed ? [] : visibleRows.map((row) => getRowId(row))),
    [collapsed, visibleRows, getRowId],
  );

  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<Set<GridRowId>>(defaultExpandedIds);

  const isControlled = controlledExpandedIds !== undefined;
  const expandedIds = isControlled ? controlledExpandedIds : uncontrolledExpandedIds;

  const handleExpandedIdsChange = useCallback(
    (ids: Set<GridRowId>) => {
      if (!isControlled) {
        setUncontrolledExpandedIds(ids);
      }
      onDetailPanelExpandedRowIdsChange?.(ids);
    },
    [isControlled, onDetailPanelExpandedRowIdsChange],
  );

  return (
    <DataGridMasterDetailColumnsContext.Provider value={columnsContextValue}>
      <MasterDetailRoot data-testid={dataTestID}>
        <MasterDetailDataGrid
          rows={visibleRows}
          columns={gridColumns}
          autoHeight={autoHeight}
          disableColumnMenu
          hideFooter
          disableRowSelectionOnClick
          columnHeaderHeight={columnHeaderHeight}
          rowHeight={rowHeight}
          getRowId={(row) => getRowId(row as R)}
          getRowClassName={getRowClassName ? (params) => getRowClassName(params as GridRowParams<R>) : undefined}
          detailPanelExpandedRowIds={expandedIds}
          onDetailPanelExpandedRowIdsChange={handleExpandedIdsChange}
          getDetailPanelContent={
            getDetailPanelContent ? (params) => getDetailPanelContent(params as GridRowParams<R>) : undefined
          }
          getDetailPanelHeight={
            getDetailPanelHeight ? (params) => getDetailPanelHeight(params as GridRowParams<R>) : undefined
          }
        />
      </MasterDetailRoot>
    </DataGridMasterDetailColumnsContext.Provider>
  );
}

export default DataGridMasterDetail;
export { MasterDetailExpandButton } from './DataGridMasterDetail.styled';
export { useDataGridMasterDetailColumns, DataGridMasterDetailColumnsContext } from './DataGridMasterDetail.context';
